/**
 * A request that never got an HTTP response (offline, connection reset, page
 * unloading, CORS/proxy rejection). Replaces the browser's bare
 * "TypeError: Failed to fetch". `message` is user-facing (views show
 * `error.message` directly); `detail` is the technical line for Sentry.
 */
export class NetworkError extends Error {
  /** Route template, e.g. `POST /v1/app/review/{review_uid}/file-review/search`. */
  readonly endpoint: string;
  readonly detail: string;

  constructor(endpoint: string, host: string, cause: unknown) {
    super('Could not reach the server. Please check your connection and try again.', { cause });
    this.name = 'NetworkError';
    this.endpoint = endpoint;
    const state = `${navigator.onLine ? 'online' : 'offline'}, page ${document.visibilityState}`;
    this.detail = `${endpoint} on ${host} (${state})`;
  }
}

/**
 * A request the backend answered with a non-2xx status. Wraps the parsed body
 * so callers keep the status: `message` is the user-facing detail and `detail`
 * is the raw `body.detail`, so code that reads either off the bare body keeps
 * working.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly detail: unknown;

  constructor(status: number, body: unknown) {
    super(extractErrorDetail(body, `Request failed with status ${status}.`));
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.detail =
      typeof body === 'object' && body !== null && 'detail' in body ? body.detail : undefined;
  }
}

/**
 * Whether an error (or the error it wraps as `cause`) is the backend refusing
 * the object. The backend answers 401 both for "no access" and for "not in
 * this organisation", deliberately indistinguishable, so views treat either as
 * "this page isn't available".
 */
export function isAccessDenied(error: unknown): boolean {
  for (let current = error; current instanceof Error; current = current.cause) {
    if (current instanceof ApiError) {
      return current.status === 401 || current.status === 403 || current.status === 404;
    }
  }
  return false;
}

/**
 * Helper function to extract meaningful error messages from various error formats
 * @param err - The error object to extract details from
 * @param defaultMessage - Custom default message to return if no error details can be extracted
 * @returns A user-friendly error message string
 */
export function extractErrorDetail(
  err: unknown,
  defaultMessage: string = 'An error occurred. Please try again.'
): string {
  // If it's null or undefined, return the default message
  if (!err) return defaultMessage;

  // If it's a string (like from the store's error value), process it
  if (typeof err === 'string') {
    // Try to extract detail from JSON string
    const detailMatch = err.match(/detail":\s*"([^"]*)"/);
    if (detailMatch && detailMatch[1]) {
      return detailMatch[1];
    }
    return err; // Return the original string if no pattern matches
  }

  // If it's an error object with a message
  if (err instanceof Error) {
    const errorMsg = err.message;

    // Try to find a JSON body in the error message
    const bodyMatch = errorMsg.match(/body:\s*(\{.*\})/);
    if (bodyMatch) {
      try {
        const body = JSON.parse(bodyMatch[1]);
        if (body.detail) return body.detail;
      } catch {
        // If parsing fails, continue with other extraction methods
      }
    }

    // Look for a detail field in the error message
    const detailMatch = errorMsg.match(/detail":\s*"([^"]*)"/);
    if (detailMatch && detailMatch[1]) {
      return detailMatch[1];
    }

    return errorMsg;
  }

  // If it's an API error object
  if (typeof err === 'object') {
    if ('body' in err) {
      const bodyMessage = extractErrorDetail(err.body, '');
      if (bodyMessage) return bodyMessage;
    }

    if ('detail' in err) {
      const detail = err.detail;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        return detail
          .map(item => extractValidationErrorItem(item))
          .filter(Boolean)
          .join('; ');
      }
      return extractErrorDetail(detail, defaultMessage);
    }

    if ('message' in err) {
      const message = err.message;
      if (typeof message === 'string') return message;
    }

    if ('error' in err) {
      const error = err.error;
      if (typeof error === 'string') return error;
      return extractErrorDetail(error, defaultMessage);
    }
  }

  return defaultMessage;
}

/**
 * Extracts the machine-readable error code from a coded backend error.
 *
 * Coded errors have the body shape `{ detail: { code, message } }` — both the
 * thrown body (global `throwOnError: true`) and the `error` field of a
 * per-call `throwOnError: false` response resolve to that same object.
 * @param err - The error object to extract the code from
 * @returns The error code, or null for uncoded errors (plain-string details,
 *   validation arrays, network failures)
 */
export function extractErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const detail = (err as { detail?: unknown }).detail;
  if (detail && typeof detail === 'object' && 'code' in detail) {
    const code = detail.code;
    if (typeof code === 'string') return code;
  }
  return null;
}

/**
 * Maps the shared public-endpoint gate failures (rate limit, recaptcha) to
 * user-facing copy.
 * @param response - The HTTP response from a per-call `throwOnError: false` request
 * @param error - The error body of that response
 * @returns A message for 429 / recaptcha 403 responses, or null so the caller
 *   falls through to its endpoint-specific handling
 */
export function gateErrorMessage(
  response: Response | null | undefined,
  error: unknown
): string | null {
  if (response?.status === 429) {
    const retryAfter = Number(response.headers.get('Retry-After'));
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      const minutes = Math.max(1, Math.ceil(retryAfter / 60));
      return `Too many attempts. Please try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
    }
    return 'Too many attempts. Please try again later.';
  }
  if (extractErrorCode(error) === 'recaptcha_failed') {
    return 'Security check failed. Please try again.';
  }
  return null;
}

function extractValidationErrorItem(item: unknown): string {
  if (!item) return '';
  if (typeof item === 'string') return item;
  if (item instanceof Error) return item.message;

  if (typeof item === 'number' || typeof item === 'boolean' || typeof item === 'bigint') {
    return String(item);
  }

  const record = item as Record<string, unknown>;
  const message =
    typeof record.msg === 'string'
      ? record.msg
      : typeof record.message === 'string'
        ? record.message
        : typeof record.detail === 'string'
          ? record.detail
          : '';
  const location = Array.isArray(record.loc) ? record.loc.join('.') : '';

  if (message && location) {
    return `${location}: ${message}`;
  }

  if (message) {
    return message;
  }

  try {
    return JSON.stringify(item);
  } catch {
    return '[Unserializable value]';
  }
}
